import { Component, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ChangeArgs } from '@syncfusion/ej2-angular-buttons';
import { EmitType } from '@syncfusion/ej2-base';
import { ChangeEventArgs } from '@syncfusion/ej2-dropdowns';
import { ToastrService } from 'ngx-toastr';
import { IApiTypedResult, IBusinessOperationQualifier, ILLGenericKeys, ITermByIndustryUsageAndCategory, Order, OrderUser, User } from '../../shared/models/index';
import { Media } from '../../shared/models/media.model';
import { AuthorizationService, OrderService, StorageManagerService } from '../../shared/services/index';
import { Utilities } from '../../shared/services/utilities';
import { OrderAdminComponent } from '../order-admin/order-admin.component';
import { OrderSummaryComponent } from '../order-summary/order-summary.component';
import { fadeInOut } from '../../shared/helpers/animations';
import { DropDownListComponent } from '@syncfusion/ej2-angular-dropdowns';

@Component({
    selector: 'order-step-1',
    templateUrl: './order-step-1.component.html',
    styleUrls: ['./order-step-1.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [fadeInOut],
    standalone: false
})
export class OrderStep1Component implements OnInit, AfterViewInit {
    // Primary key
    orderRevisionId: number;
    // Object model for Order
    order: Order;
    // Hold on to original values for these fields for saving later
    beforeOrderStatus: string;
    beforeConfirmedDate: Date;
    // Angular form that contains controls
    orderStep1Form: UntypedFormGroup;

    // Controls state of the form
    isServiceCategorySelected: boolean;

    freezeChangeEventsTemporarily: boolean;

    // ------------------------------------------------------------
    // ServiceType pulldown
    // ------------------------------------------------------------
    // Reference data list
    // Possible pulldown list values, contents change by service type
    serviceCategoryToServiceTypeAssoc: IBusinessOperationQualifier[];
    serviceTypesLOV: ILLGenericKeys[];
    serviceGroupsLOV: String[];
    servTypesWaterMark = 'Select Service Type';
    servGroupWaterMark = 'Select Service Group';
    isServiceTypeSelected = false;
    isServiceGroupSelected = false;
    ddwFields: Object = { text: 'label', value: 'code' };
    ddwHeight = '220px';
    bIsOrder = true;
    inactiveOrderEmailRecipientList: any[] = [];
    inactiveOrderOwnersList: any[] = [];

    // ------------------------------------------------------------
    // ServiceType pulldown
    // ------------------------------------------------------------
    // Reference data list
    serviceTypeToDeliveryPriorityAssoc: IBusinessOperationQualifier[];

    // Regular priority box
    hasRegularPriority = true;

    // Expedited priority box
    hasExpeditedPriority = true;

    // Emergency priority box
    hasEmergencyPriority = true;

    // Order type
    showOrderType = false;

    // List of all employees in company
    allEmployeesInCompany: User[] = null;

    // Save button caption
    saveButtonCaption = 'Save as Draft';

    // ------------------------------------------------------------
    // orderOwners multi-select pulldown
    // ------------------------------------------------------------
    // Map keys/values for owners pulldown list
    orderOwnersFields = { 'text': 'label', 'value': 'code' };
    // Placeholder when order owners pulldown empty
    orderOwnersWaterMark = 'Select Owners';
    // Will hold possible values for list
    orderOwnersLOV: ILLGenericKeys[];


    // ------------------------------------------------------------
    // orderRecipients multi-select pulldown
    // ------------------------------------------------------------
    // Map keys/values for email  pulldown list
    orderRecipientsFields = { 'text': 'label', 'value': 'code' };
    // Placeholder when order owners pulldown empty
    orderRecipientsWaterMark = 'Select Recipients';
    // Will hold possible values for list
    orderRecipientsLOV: ILLGenericKeys[];


    // ------------------------------------------------------------
    // Order admin box
    // ------------------------------------------------------------
    @ViewChild('orderAdminComponent')
    orderAdmin: OrderAdminComponent;

    @ViewChild('orderSummaryComponent')
    orderSummary: OrderSummaryComponent;

    // Delivery doc associated with this order
    // (not the source docs)
    deliveryDoc: Media = null;

    // If user deletes an existing delivery doc,
    // there's no angular form changes, but we need to force
    // a page reload.
    deliveryDocStatusChange: boolean;
    // No source docs on step 3

    orderStatuses: ITermByIndustryUsageAndCategory[] = null;

    // ------------------------------------------------------------
    // Page state
    // ------------------------------------------------------------
    // Are we currently loading or saving?
    pageState: string = null;
    // Can the current user edit the current order?
    canEdit: boolean;
    // Does the current user have admin order (RT employee) permissions
    canOrderAdmin: boolean;
    // Can user request cancellation?
    canRequestCancellation: boolean;
    // Error message to display on bottom of screen.
    errorMessage = '';

    dropElement: HTMLElement = null;

    // ------------------------------------------------------------
    // Post save settings
    // ------------------------------------------------------------
    // Save the order object, when finished then optionally finish a deliverDoc
    // upload.  There's a lot of async steps, so put information about
    // saving orders and docs into class variables that can be set
    // in saveData and accessed in postSaveData to tie everything together.

    // Message from server about saving the order
    postSaveDataMessage: string;
    // URL to go to after save is finished.  Note we need the
    // new OrderRevisionId of the newly saved order revision
    // to generate this URL (usually).
    postSaveDataRedirectUrl: string;
    // OrderRevisionId from after we save order changes (but we haven't uploaded files yet so we need this stored)
    postSaveDataNewOrderRevisionId: number;
    // Track if source docs are finished uploading after order save
    postSaveWaitingForSourceDocUpload: boolean;
    // Track if delivery docs are finished uploading after order save
    postSaveWaitingForDeliveryUpload: boolean;

    // Should serviceType pulldown be enabled?
    isServiceTypeEnabled = false;
    // Should deliveryPriority radio buttons be enabled?
    isDeliveryPriorityEnabled = false;
    ingestFileValue: boolean;

    constructor(private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private authorizationService: AuthorizationService,
        public orderService: OrderService,
        private storageManagerService: StorageManagerService,
        private toastr: ToastrService,
        private router: Router,
        private utilService: Utilities) { }


    // ------------------------------------------------------------
    // Initialize
    // ------------------------------------------------------------

    ngOnInit(): void {
        this.pageState = this.orderService.PAGE_STATE_LOADING;

        // Users can't change companies in the middle of viewing/editing an order
        this.utilService.toggleClientDropdownState(true);

        // Force some permissions off in case of ng routing doesn't do a full refresh
        this.order = null;
        this.beforeOrderStatus = null;
        this.beforeConfirmedDate = null;
        this.canEdit = false;
        this.canOrderAdmin = false;
        this.canRequestCancellation = false;
        this.deliveryDoc = null;
        this.deliveryDocStatusChange = false;
        // no source docs on step 1

        this.isServiceCategorySelected = false;
        this.freezeChangeEventsTemporarily = false;

        // Extract primary key from query string
        this.orderRevisionId = this.orderService.getNumberOrNull(this.activatedRoute.snapshot.params.orderRevisionId);

        // The guard says you need read permission to get to this page.
        // User might also have edit permissions.
        this.canEdit = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);
        this.canOrderAdmin = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN);

        // If new, force no admin
        if (!this.orderRevisionId) {
            this.canOrderAdmin = false;
        }

        // Tell angular form what fields it has.
        this.orderStep1Form = this.fb.group({
            orderName: ['', { validators: [Validators.required] }],
            serviceCategory: ['', this.showOrderType ? Validators.required : null],
            deliveryPriority: ['', { validators: [Validators.required] }],
            serviceTypeId: ['', { validators: [Validators.required] }],
            drpOrderOwners: [''],
            drpOrderRecipients: [''],
            serviceGroup: ['', { validators: [Validators.required] }],
        });

       // set focus on the invisible div so the firest tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
       this.initTabIndex();
    }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById('firstDiv');

    if (main) {
      main.focus();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      // Get data from the resolver and populate various dropdownlists
      this.loadData();

      // This list changes based on order type
      if (this.order) {
        //For existing orders, once the service group is saved to the db, we need to pass the service group to the service type lov
        this.populateServiceTypesLOV(this.order.serviceCategory, this.order.serviceGroup);
        if (this.orderAdmin && this.canOrderAdmin) {
            if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_FULL_CONVERSION  || this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_PARTIAL_CONVERSION) {
                this.orderAdmin.showIngestFile(true);
            } else {
                this.orderAdmin.showIngestFile(false);
            }
        }
      }

      this.checkOrderType();
      this.populateServiceGroupLOV();
      // Order Owner list and Order Recipient list
      this.populateEmployeeLists();

      // Populate form controls with the order data
      this.populateFormControls();

      // There's a weird timing problem where I can't get this to load
      // normally, so we have to wait till now to force the data to load
      if (this.orderSummary) {
        this.orderSummary.loadData();
      }

      this.pageState = this.orderService.PAGE_STATE_READY;

      this.isServiceTypeEnabled = this.isServiceCategorySelected && this.canEdit;
      this.isDeliveryPriorityEnabled = this.isServiceTypeSelected && this.canEdit;
    });
  }

  // ------------------------------------------------------------
    // Load data
    // ------------------------------------------------------------

    // Get data from the resolver and populate various dropdownlists
    // and main order fields and store it in class state.
    loadData(): void {
        // Get data from resolver used in routing module
        const orderDataResolved = this.activatedRoute.snapshot.data['orderDataResolved'];
        if (orderDataResolved.error) {
            this.toastr.error(orderDataResolved.error);
        } else {
            if (orderDataResolved) {
                // OrderResolver returns a fixed array of data.

                // Employees of this company
                if (!orderDataResolved[0]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get list of employees in company', false);
                }
                this.allEmployeesInCompany = orderDataResolved[0];

                // Reference lists
                if (!orderDataResolved[1]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get reference data (service type)', false);
                }
                this.serviceCategoryToServiceTypeAssoc = orderDataResolved[1];

                if (!orderDataResolved[2]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get reference data (priority)', false);
                }
                this.serviceTypeToDeliveryPriorityAssoc = orderDataResolved[2];

                // Main order
                if (!orderDataResolved[3] && this.orderRevisionId) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order', false);
                }

                let orderApiResult: IApiTypedResult<Order> = orderDataResolved[3];
                if (orderApiResult) {
                    if (orderApiResult.isSuccess) {
                        this.order = orderApiResult.data;
                        // Special case - if order owner contains a record that is not present, display warning on UI
                        this.inactiveOrderOwnersList = this.orderService.GetInactiveOrderUsers(this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_OWNER);
                        // If order email recipient contains a record that is not present, display warning on UI
                        this.inactiveOrderEmailRecipientList = this.orderService.GetInactiveOrderUsers(this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT);;

                        // Remember original values for certain fields
                        this.beforeOrderStatus = this.order.orderStatus;
                        this.beforeConfirmedDate = this.order.confirmedDueDate;

                        // Only user with "SSDP.CanUpateOrder" permission can edit an order if the order status is "Order Delivered - Awaiting Approval" or "completed"
                        if (!this.canOrderAdmin && (this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
                            || this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_COMPLETE)) {
                            this.canEdit = false;
                        }

                        // Don't allow canOrderAdmin if draft
                        if (this.order.orderStatus === this.orderService.ORDER_STATUS_DRAFT) {
                            this.canOrderAdmin = false;
                        }

                        // If still canOrderAdmin, recaption save
                        if (this.canOrderAdmin) {
                            this.saveButtonCaption = 'Update Order';
                        }

                        // Non-admin users can cancel pending or inprocess if they have the perm
                        if (!this.canOrderAdmin
                            && ([this.orderService.ORDER_STATUS_PENDING, this.orderService.ORDER_STATUS_IN_PROCESS].indexOf(this.order.orderStatus) > -1)
                            && this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_CANCEL_ORDER)) {
                            this.canRequestCancellation = true;
                        }

                        // Pull out the deliveryDoc and sourceDocs separately
                        // order.sourceDocuments is an array mixed with both deliveryDoc and SourceDocs.
                        if (this.order && this.order.sourceDocuments) {
                            this.deliveryDoc = null;
                            let deliveryDocs = this.order.sourceDocuments.filter(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_DELIVERY);
                            if (deliveryDocs && deliveryDocs.length > 0) {
                                this.deliveryDoc = deliveryDocs[0];
                            }
                            // Step 1 - no sourceDocs
                        }
                    } else {
                        this.orderService.handleOrderError(this, orderApiResult, 'Failed to load order', false);
                    }
                }

                if (!orderDataResolved[5]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order statuses', false);
                }
                this.orderStatuses = orderDataResolved[5];
            }
        }
    } // end loadData

    // ------------------------------------------------------------
    // Populate form controls
    // ------------------------------------------------------------

    // Populate pulldown list of serviceType based on serviceCategory.
    populateServiceTypesLOV(serviceCategory: string, serviceGroup: string): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        this.serviceTypesLOV = [];

        // If all data present,
        // return a list of ILLGenericKeys filtered
        // from the list of IBusinessOperationQualifier by parentCode===serviceCategory
        if (this.serviceCategoryToServiceTypeAssoc) {
          this.serviceTypesLOV = this.serviceCategoryToServiceTypeAssoc
              .filter(x => {
                  const matchesParentFilter = x.parentTermListName === serviceGroup;
                  const matchesServiceType = x.childTermListName === 'Service Type';
                  const matchesServiceCategory = serviceCategory ? x.parentTermValueName === serviceCategory : true;
                  return matchesParentFilter && matchesServiceType && matchesServiceCategory;
              })
              .map(x =>
                  new class implements ILLGenericKeys {
                      code = x.childTermValueCode;
                      label = x.childTermValueName;
                      id = null;
                      name = null;
                      relatedKeyLabels = x.parentTermListName;
                      relatedKeys = null;
                  });
        }
    }

    // Populate pulldown list of serviceType based on serviceCategory.
    populateServiceGroupLOV(): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        this.serviceGroupsLOV = [];

          // If all data present,
          // return a list of strings filtered
        if (this.serviceCategoryToServiceTypeAssoc) {
          this.serviceGroupsLOV = this.serviceCategoryToServiceTypeAssoc
              .map(x => x.parentTermListName)
              .filter((value, index, self) => self.indexOf(value) === index)
              .sort((a, b) => a.localeCompare(b));
        }

        if(this.order)
        {
          this.orderStep1Form.controls.serviceGroup.setValue(this.order.serviceGroup);
        }

    }

     onlyUnique(value, index, array) {
        return array.indexOf(value) === index;
      }

    // Populate list of employees in Order Owners and Order Recipients lists
    populateEmployeeLists(): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        if (this.allEmployeesInCompany) {
            // Reformat from user objects list to pulldown list of ILLGenericKeys
            this.orderOwnersLOV = this.allEmployeesInCompany
                .map(emp => new class implements ILLGenericKeys {
                    code = emp.userGuid;
                    label = emp.firstName + ' ' + emp.lastName;
                    id = null;
                    name = null;
                    relatedKeyLabels = null;
                    relatedKeys = null;
                });
            // Sort by name
            this.orderOwnersLOV.sort((a, b) => a.label.localeCompare(b.label));
            // Copy same employees to email list (for Object to "any" to use newer .assign method without VS warning
            this.orderRecipientsLOV = this.orderOwnersLOV.map(x => (Object as any).assign({}, x));
        }
    }

    // Populate the order data
    populateFormControls(): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        this.orderStep1Form.reset();

        if (this.orderRevisionId == null) {
            // New record, use defaults
            const userGuid = this.storageManagerService.read('userGuid');
            this.orderStep1Form.patchValue({
                serviceCategory: '',
                serviceTypeId: null,
                deliveryPriority: '',
                orderName: null,
                drpOrderOwners: [userGuid],
                drpOrderRecipients: [],
                serviceGroup: ''
            });
        } else {
            this.orderStep1Form.patchValue({
                serviceCategory: this.order ? this.order.serviceCategory : 0,
                serviceTypeId: this.getServiceTypeCode(),
                serviceGroup: this.order ? this.order.serviceGroup : null,
                deliveryPriority: this.order ? this.order.deliveryPriority : 0,
                orderName: this.order.orderName,
                // Feed a string array of userGuids to pulldown lists
                drpOrderOwners: this.orderService.getUserGuidsFromOrderUsers(
                    this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_OWNER),
                drpOrderRecipients: this.orderService.getUserGuidsFromOrderUsers(
                    this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT)
            });
        }

        // Track if certain controls are filled out because other controls' enabled state depend on them
        if (this.orderStep1Form.get('serviceCategory').value) {
            this.isServiceCategorySelected = true;
        } else {
            this.isServiceCategorySelected = false;
        }
        if (this.orderStep1Form.get('serviceTypeId').value) {
            this.isServiceTypeSelected = true;
        } else {
            this.isServiceTypeSelected = false;
        }
        if(this.orderStep1Form.get('serviceGroup').value) {
          this.isServiceGroupSelected = true;
        }
        else {
          this.isServiceGroupSelected = false;
        }

        if (this.order) {
            this.updateDeliveryPrioritiesVisibility(this.getServiceTypeCode());
        }
    }

    getServiceTypeCode(): string {
        // Okay so the main order object has this sub-object
        // order.splService {
        //   id: "",
        //   label: "",
        //   code: "",
        //   relatedKeys: [],
        //   relatedKeyLabels: ""
        // }
        //
        // So I assumed I would pull order.splService.code and find something like "SPL-LL-AH-F"
        // However after I load, I only get order.splService.label filled in,
        // which seems really a bad way, but I have to deal with it.
        // Eventually someone will probably fix the backend to supply "code",
        // so update the angular to handle that also.
        let code = null;
        if (this.order && this.order.splService) {
            // The preferred method, match by code, but not used as of 6/15/2020
            // Otherwise try to match by label.
            if (this.order.splService.code) {
                code = this.order.splService.code;
            } else if (this.order.splService.label) {
                // Force array to "any" to use newer ".find" method without VS error
                const match = (this.serviceTypesLOV as any).find(x => x.label === this.order.splService.label);
                if (match) {
                    code = match.code;
                }
            }
        }
        return code;
    }


    // ------------------------------------------------------------
    // Change Events
    // ------------------------------------------------------------

    // Track if an serviceCategory is selected
    serviceCategoryChanged: EmitType<ChangeArgs> = (event: any) => {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        const oldServiceCategory = this.orderStep1Form.controls.serviceCategory.value;
        const newServiceCategory = event.value;
        let isDeliveryPrioritySelected = false;
        if (this.orderStep1Form.controls && this.orderStep1Form.controls.deliveryPriority && this.orderStep1Form.controls.deliveryPriority.value) {
            isDeliveryPrioritySelected = true;
        }
        // If service category changes
        // blank out dependent fields service type and delivery priority.
        // But don't bother if serviceType and deliveryPriority are still blank.
        if ((this.isServiceTypeSelected || isDeliveryPrioritySelected)
            && oldServiceCategory
            && (oldServiceCategory !== newServiceCategory)) {
            // Confirm with user that we're blanking out dependent fields
            if (!confirm(`Are you sure you want to change the type of order?  This change will blank out dependent values such as service type and delivery priority.`)) {
                setTimeout(() => {
                    this.freezeChangeEventsTemporarily = true;
                    this.orderStep1Form.patchValue({ serviceCategory: oldServiceCategory });
                    this.freezeChangeEventsTemporarily = false;
                }, 200);
                return;
            }

            // User does want to continue with the change
            this.orderStep1Form.patchValue({
                serviceTypeId: null,
                deliveryPriority: null
            });
            this.isServiceTypeSelected = false;

            this.isServiceCategorySelected = true;
            if (newServiceCategory) {
                var serviceGroup = this.orderStep1Form.controls.serviceGroup.value;
                this.populateServiceTypesLOV(newServiceCategory, serviceGroup);
                this.populateServiceGroupLOV();
            }

            this.updateDeliveryPrioritiesVisibility(null);
        } else {
            this.isServiceCategorySelected = true;
            if (newServiceCategory) {
                var serviceGroup = this.orderStep1Form.controls.serviceGroup.value;
                this.populateServiceTypesLOV(newServiceCategory, serviceGroup);
            }
        }

        if (this.orderAdmin) {
            if (newServiceCategory === this.orderService.SERVICE_CATEGORY_FULL_CONVERSION  || newServiceCategory === this.orderService.SERVICE_CATEGORY_PARTIAL_CONVERSION) {
                this.orderAdmin.showIngestFile(true);
            } else {
                this.orderAdmin.showIngestFile(false);
            }
        }
    }

    isOrderTypeSelected(): boolean {
      return this.orderStep1Form.get('serviceCategory').value !== '';
    }

    // Track if a service type is selected
    serviceTypeChanged: EmitType<ChangeEventArgs> = (event: any) => {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        this.isServiceTypeSelected = false;
        if (event.itemData) {
            this.isServiceTypeSelected = true;
            const serviceCategory = this.orderStep1Form.controls.serviceCategory.value;
            const oldServiceType = this.orderStep1Form.controls.serviceTypeId.value;
            const newServiceType = event.value;
            let isDeliveryPrioritySelected = false;
            if (this.orderStep1Form.controls && this.orderStep1Form.controls.deliveryPriority && this.orderStep1Form.controls.deliveryPriority.value) {
                isDeliveryPrioritySelected = true;
            }

            // If delivery priority was already selected,
            // and we are changing service type,
            // confirm with user
            // that we're about to wipe it out
            if (isDeliveryPrioritySelected
                && oldServiceType
                && (oldServiceType !== newServiceType)) {

                this.freezeChangeEventsTemporarily = true;
                if (confirm(
                    `Are you sure you want to change the serviceType?  This change will blank out dependent values, namely the delivery priority.`)) {
                    // User chose OK
                    // Delivery priority depends on service type, so blank it out upon change
                    this.orderStep1Form.patchValue({
                        deliveryPriority: null
                    });
                    this.updateDeliveryPrioritiesVisibility(newServiceType);
                    this.freezeChangeEventsTemporarily = false;
                } else {
                    // User chose cancel
                    // Undo selection
                    setTimeout(() => {
                        this.orderStep1Form.patchValue({ serviceTypeId: oldServiceType });
                        this.isServiceTypeSelected = true;
                        this.freezeChangeEventsTemporarily = false;
                    }, 200);
                }
            } else {
                // Don't need to confirm anything with user because dependent fields
                // are blank, go ahead and update delivery priorities visibility
                this.updateDeliveryPrioritiesVisibility(newServiceType);
            }
        }
    }

    // Track if a service Group is selected
    serviceGroupChanged: EmitType<ChangeEventArgs> = (event: any) => {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        const oldServiceGroup = this.orderStep1Form.controls.serviceGroup.value;

        if (this.order && event.itemData.value !== this.order.serviceGroup) {
            if (confirm(
                `Are you sure you want to change the service group?  This change will blank out dependent values.`)) {
                // reset the whole order form
                this.orderStep1Form.reset();
                // Order Owner list and Order Recipient list
                this.populateEmployeeLists();

                if (this.order) {
                    this.orderStep1Form.patchValue({
                        serviceGroup: event.itemData.value,
                        drpOrderOwners: this.orderService.getUserGuidsFromOrderUsers(
                            this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_OWNER),
                        drpOrderRecipients: this.orderService.getUserGuidsFromOrderUsers(
                            this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT)
                    });
                    this.resetOrderData();
                } else {
                    this.orderStep1Form.patchValue({
                        serviceGroup: event.itemData.value,
                    });
                }
            } else {
                setTimeout(() => {
                    this.freezeChangeEventsTemporarily = true;
                    this.orderStep1Form.patchValue({ serviceGroup: "Composition Publishing (Publishing)" });
                    this.freezeChangeEventsTemporarily = false;
                }, 200);
                return;
            }
        }

        if (event.itemData) {
          this.isServiceGroupSelected = true;
          if(this.order)
              this.order.serviceGroup = event.itemData.value;
            this.serviceTypesLOV = [];
            this.checkOrderType(event.itemData.value);
            if(!this.showOrderType) {
              this.orderStep1Form.controls.serviceCategory.setValue('');
              this.isServiceCategorySelected = true;
              this.populateServiceTypesLOV(null, event.itemData.value);
              this.serviceTypesLOV = this.serviceTypesLOV.filter(x => x.relatedKeyLabels  === event.itemData.value);
            }
            this.updateDeliveryPrioritiesVisibility(null);
        }
    }

    resetOrderData() {
        this.order.baselineOrderId = null;
        this.order.deliveryPriority = null;
        this.order.splService = new ILLGenericKeys();
        this.order.orderStatus = null;
        this.order.actualDeliveryDate = null;
        this.order.confirmedDueDate = null;
        this.order.productName = null;
        this.order.dataCollectionForm = null;
        this.order.additionalInformation = null;
        this.order.acctMgrInstructions = null;
        this.order.sourceDocuments = [];
        this.order.comments = null;
        this.order.commentsChangeUser = null;
        this.order.commentsChangeDate = null;
        this.order.orderComments = [];
        this.order.orderName = null;
        this.order.colOrDrugFactChangeInstruction = null;
        this.order.principalDisplayPanelChangeInstruction = null;
        this.order.LLMetadataChangeInstruction = null;
        this.order.orderDescription = null;
        this.order.serviceCategory = null;
        this.order.serviceGroup = null;
    }


    checkOrderType(serviceGroup: string = null) {
        if (serviceGroup) {
            this.showOrderType = [this.orderService.SERVICE_GROUP_LABEL_CONVERSION, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_PUBLISHING].includes(serviceGroup);
        }
        else if (this.orderStep1Form.controls.serviceGroup.value) {
            this.showOrderType = [this.orderService.SERVICE_GROUP_LABEL_CONVERSION, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_PUBLISHING].includes(this.orderStep1Form.controls.serviceGroup.value);
        } else if (this.order?.serviceGroup) {
            this.showOrderType = [this.orderService.SERVICE_GROUP_LABEL_CONVERSION, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_PUBLISHING].includes(this.order.serviceGroup);
        } else {
            this.showOrderType = true;
        }
    }

    isDeliveryPrioritySelected(): boolean {
      return this.orderStep1Form.get('deliveryPriority').value !== '';
    }

    // Hide certain delivery priorities based on certain service catgories and service types
    updateDeliveryPrioritiesVisibility(serviceType: string) {
        // Start by showing all delivery priorities
        this.hasRegularPriority = true;
        this.hasExpeditedPriority = true;
        this.hasEmergencyPriority = true;

        // use ref data to determine all delivery priorities.
            if (serviceType) {

                let currentDeliveryPriority = null;
                if (this.orderStep1Form.controls && this.orderStep1Form.controls.deliveryPriority && this.orderStep1Form.controls.deliveryPriority.value) {
                    currentDeliveryPriority = this.orderStep1Form.controls.deliveryPriority.value;
                }

                // If REGULAR priority is not supported for this service type, hide it
                if (!this.serviceTypeToDeliveryPriorityAssoc
                    .some(x => x.parentTermValueCode === serviceType && x.childTermValueCode === this.orderService.DELIVERY_PRIORITY_REGULAR)) {
                    if (currentDeliveryPriority === this.orderService.DELIVERY_PRIORITY_REGULAR) {
                        // Make sure we don't hide the currently selected delivery priority value
                        this.orderStep1Form.patchValue({
                            deliveryPriority: ''
                        });
                    }
                    this.hasRegularPriority = false;
                }

                // If EXPEDITED priority is not supported for this service type, hide it
                if (!this.serviceTypeToDeliveryPriorityAssoc
                    .some(x => x.parentTermValueCode === serviceType && x.childTermValueCode === this.orderService.DELIVERY_PRIORITY_EXPEDITED)) {
                    if (currentDeliveryPriority === this.orderService.DELIVERY_PRIORITY_EXPEDITED) {
                        // Make sure we don't hide the currently selected delivery priority value
                        this.orderStep1Form.patchValue({
                            deliveryPriority: ''
                        });
                    }
                    this.hasExpeditedPriority = false;
                }

                // If EMERGENCY priority is not supported for this service type, hide it
                if (!this.serviceTypeToDeliveryPriorityAssoc
                    .some(x => x.parentTermValueCode === serviceType && x.childTermValueCode === this.orderService.DELIVERY_PRIORITY_EMERGENCY)) {
                    if (currentDeliveryPriority === this.orderService.DELIVERY_PRIORITY_EMERGENCY) {
                        // Make sure we don't hide the currently selected delivery priority value
                        this.orderStep1Form.patchValue({
                            deliveryPriority: ''
                        });
                    }
                    this.hasEmergencyPriority = false;
                }
            }
    }

    onIngestFilechanged(event: boolean) {
        this.ingestFileValue = event;
    }

    // ------------------------------------------------------------
    // Order admin is a subcomponent, and emits up certain events
    // usually about the delivery doc
    // ------------------------------------------------------------
    onOrderAdminFileUpload(event) {
        if (event === 'success') {
            // Successfully uploaded delivery doc
            this.postSaveWaitingForDeliveryUpload = false;
            this.postSaveData();
        } else if (event === 'failure') {
            // Attempted to upload delivery doc but failed
            this.pageState == this.orderService.PAGE_STATE_READY;
            this.toastr.error('Unexpected error uploading delivery document');
        } else if (event === 'deleteExistingDeliveryDoc') {
            // Request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = true;
                this.deliveryDoc.isDeleted = true;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;
        } else if (event === 'undeleteExistingDeliveryDoc') {
            // Undo request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = false;
                this.deliveryDoc.isDeleted = false;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;
        } else if (event === 'onIngestFileChange') {

            if (this.deliveryDoc) {
                this.deliveryDoc.ingestFile = this.ingestFileValue;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;

        }
    }

    // ------------------------------------------------------------
    // Save data
    // ------------------------------------------------------------
    saveData(nextPage: string) {
        // Special case - "Submit Order" has additional validation rules.
        // They apply for fields that are on step 3.
        // Keep this code on all pages for consistency.
        if (nextPage === 'SUBMIT') {
            // doesn't apply to step 1
            // ignore this
        }

        this.pageState = this.orderService.PAGE_STATE_SAVING;

        // Clear out data from prior save
        this.postSaveDataMessage = null;
        this.postSaveDataNewOrderRevisionId = null;
        this.postSaveDataRedirectUrl = null;
        this.postSaveWaitingForDeliveryUpload = false;
        this.postSaveWaitingForSourceDocUpload = false;

        // Where to go after save
        let nextPageUrl = '';
        switch (nextPage) {
            case 'STEP2':
                nextPageUrl = '/order-step-2';
                break;
            default:
                nextPageUrl = '/order-step-1';
                break;
        }

        this.errorMessage = '';

        const modifiedByUser = this.storageManagerService.read('userData').name;

        // Request order cancellation is different enough let's handle it separately.
        if (nextPage === 'CANCEL') {
            this.orderService.requestOrderCancellation(this.canRequestCancellation, this.order, modifiedByUser, '/order-step-1', false);
            this.pageState = this.orderService.PAGE_STATE_READY;
        } else {
            let dirty = this.orderService.isAngularFormDirty(this.orderStep1Form);
            // Also check fields in order admin box
            if (this.canOrderAdmin && this.orderAdmin.orderAdminForm) {
                dirty = dirty || this.orderService.isAngularFormDirty(this.orderAdmin.orderAdminForm);
            }

            // stop here if form is invalid
            if (this.orderStep1Form.invalid) {
                this.pageState = this.orderService.PAGE_STATE_READY;
                this.toastr.warning('Data is invalid.');
                return;
            } else {
                // Only save if dirty
                if (dirty && (this.canEdit || this.orderAdmin)) {
                    if (!this.order) {
                        // New record
                        this.order = new Order();
                        this.order.orderStatus = this.orderService.ORDER_STATUS_DRAFT;
                        // Accept optional query string param for dataCollectionForm
                        let dataCollectionForm = this.activatedRoute.snapshot.queryParamMap.get('llRevisionId');
                        if (dataCollectionForm) {
                            this.order.dataCollectionForm = parseInt(dataCollectionForm);
                        }
                    }

                    // Put back in prior values for certain fields
                    this.order.beforeOrderStatus = this.beforeOrderStatus;
                    this.order.beforeConfirmedDate = this.beforeConfirmedDate;

                    // Before we save any data, check if there are files waiting to be uploaded
                    let sourceDocsWaitingToBeUploaded = false;
                    // no source docs on step 1
                    let deliveryDocsWaitingToBeUploaded = false;
                    if (this.canOrderAdmin && this.orderAdmin.adminFileUpload && this.orderAdmin.adminFileUpload.getFilesData() && this.orderAdmin.adminFileUpload.getFilesData().length > 0) {
                        deliveryDocsWaitingToBeUploaded = true;
                    }

                    // Save the order attributes first
                    this.order.serviceCategory = this.f.serviceCategory.value;
                    this.order.serviceGroup = this.f.serviceGroup.value;
                    this.order.splService = this.getServiceTypeObj(this.f.serviceTypeId.value);
                    this.order.deliveryPriority = this.f.deliveryPriority.value;
                    this.order.orderName = this.f.orderName.value;

                    const orderOwners: OrderUser[] = this.orderService.userGuidsToOrderUsers(
                        this.allEmployeesInCompany, this.f.drpOrderOwners.value, this.orderService.ORDER_USER_TYPE_ORDER_OWNER);
                    const orderRecipients: OrderUser[] = this.orderService.userGuidsToOrderUsers(
                        this.allEmployeesInCompany, this.f.drpOrderRecipients.value, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT);
                    this.order.orderUsers = orderOwners.concat(orderRecipients);

                    // Save the admin fields if we're allowed
                    if (this.order && this.canOrderAdmin) {
                        this.order.orderStatus = this.orderAdmin.orderAdminForm.controls.orderStatus.value;
                        this.order.confirmedDueDate = this.utilService.getLocalDateString(this.orderAdmin.orderAdminForm.controls.confirmedDueDate.value);

                        if (deliveryDocsWaitingToBeUploaded) {
                            // DeliveryDoc supplied, mark the actual delivery date
                            this.order.actualDeliveryDate = this.utilService.getLocalDateString(new Date());
                        } else if (this.deliveryDoc && this.deliveryDoc.isDeleted) {
                            // DeliveryDoc was requested to be deleted, blank
                            // out actual delivery date
                            this.order.actualDeliveryDate = null;
                        }
                    }

                    // Save to database
                    this.orderService.saveOrder(this.order, modifiedByUser).subscribe((response) => {
                        // Coerce to specific type
                        const responseTyped: IApiTypedResult<Order> = response as IApiTypedResult<Order>;
                        if (responseTyped.isSuccess) {
                            // Gather up settings so after async upload finishes we know where to go
                            this.postSaveDataMessage = responseTyped.message;
                            this.postSaveDataNewOrderRevisionId = responseTyped.data.orderRevisionId;
                            this.postSaveDataRedirectUrl = `${nextPageUrl}/${this.postSaveDataNewOrderRevisionId}`;

                            if (nextPage === 'SUBMIT') {
                                // Step 1 doesn't have submit, but steps 1, 2, 3 save methods
                                // are so complex, it makes my life easier to keep the same structure
                                // across all
                                this.toastr.error('invalid submit');
                                return;
                            } else {
                                // Regular save of Order object successful, now do optional file uploads.
                                //
                                // We just set the property on this component postSavedatNewOrderRevisionId
                                // which is shared with the child component OrderAdminComponent,
                                // sounds great BUT angular timing messes it up.  The child component
                                // can't see the value immediately.  The child component can't do setTimeout
                                // because the upload is already in progress.  So, we make the parent setTimeout
                                // (sleep) right now so angular child component catches up with bound value for
                                // and can save the uploaded doc to correct postSavedatNewOrderRevisionId
                                setTimeout(() => {

                                    // Now that the order is saved, upload docs to *new* OrderRevisionId from
                                    // potentially sourceDocs and delivery (inside child component)
                                    if (!sourceDocsWaitingToBeUploaded && !deliveryDocsWaitingToBeUploaded) {
                                        // No files to upload, we're done.
                                        this.postSaveData();
                                    } else {
                                        // no sourceDoc files from this page
                                        // Asynchronously upload delivery file from admin child componet
                                        if (deliveryDocsWaitingToBeUploaded) {
                                            // upload the files
                                            this.postSaveWaitingForDeliveryUpload = true;
                                            this.orderAdmin.adminFileUpload.upload(this.orderAdmin.adminFileUpload.getFilesData());  // this is async
                                        }
                                        // Nothing left for now, wait for events to get triggered.
                                        return;
                                    }
                                }, 100);
                            }
                        } else {
                            this.orderService.handleOrderError(this, responseTyped, 'Order save failed', true)
                        }
                    },
                        error => this.orderService.handleOrderError(this, error, 'Order save failed', true)
                    );
                    this.orderStep1Form.markAsPristine();
                } else {
                    this.pageState = this.orderService.PAGE_STATE_READY;
                    // if no changes to save, but user wants to go to next page, that's fine
                    if (nextPage === 'STEP2') {
                        // Force angular to rerun ngOnInit to reload data when navigating to address
                        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
                        this.router.onSameUrlNavigation = 'reload';

                        this.router.navigate([`${nextPageUrl}/${this.orderRevisionId}`]).then(() => {
                            this.pageState = this.orderService.PAGE_STATE_READY;
                            window.scrollTo(0, 0);
                        });
                    } else {
                        this.pageState = this.orderService.PAGE_STATE_READY;
                        this.toastr.warning('No changes to save.');
                    }
                }
            }
        }
    }

    // Helper function for dealing with angular interaction
    // between order types and service types and underlying reference data
    getServiceTypeObj(serviceTypeId: string): ILLGenericKeys {
        const matches = this.serviceTypesLOV.filter(x => x.code === serviceTypeId);
        if (matches) {
            // Huh?  If these values are null during save I get an error 'source required'
            // Set them this way and it works.  I don't know why.
            matches[0].relatedKeys = [];
            matches[0].relatedKeyLabels = '';
            return matches[0];
        } else {
            return null;
        }
    }

    // After order is saved AND all files are uploaded via order admin
    // and Step 2 file uploader (not easy because they are all async)
    // then go ahead and give a message and redirect.
    // Only step 2 has dual file uploaders - source & delivery,
    // but for consistency I'm copy/pasting this function across
    // all three steps.
    postSaveData() {
        // Are we still waiting for sourceDoc upload or delivery upload?
        if (this.postSaveWaitingForSourceDocUpload || this.postSaveWaitingForDeliveryUpload) {
            // Yup, keep waiting.
            return;
        }

        this.toastr.success(this.postSaveDataMessage);

        // Force angular to rerun ngOnInit to reload data when navigating to address
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        this.router.navigate([this.postSaveDataRedirectUrl]).then(() => {
            this.pageState = this.orderService.PAGE_STATE_READY;
            // Clear out admin file upload control if relevant
            if (this.orderAdmin && this.orderAdmin.adminFileUpload) {
                this.orderAdmin.adminFileUpload.clearAll();
            }
            window.scrollTo(0, 0);
        });
    }

    ddwChanged(event) {
        if (event.isInteracted) {
            this.orderStep1Form.markAsDirty();
        }
    }
    // ------------------------------------------------------------
    // Misc
    // ------------------------------------------------------------

    // convenience getter for easy access to form fields
    get f() { return this.orderStep1Form.controls; }

    // ------------------------------------------------------------
    // Destroy
    // ------------------------------------------------------------
    ngOnDestroy(): void {
        this.utilService.toggleClientDropdownState(false);
    }
}
